<script setup lang="ts">
import { computed } from 'vue';
import type { PlayerProfile, PlayerPosition } from '@/types/models';

interface Props {
  assignments: Record<PlayerPosition, PlayerProfile | null>;
  selectedPosition?: PlayerPosition | null;
  shiftPreset?: 'STANDARD' | 'INFIELD_IN' | 'PULL_SHIFT' | 'NO_DOUBLES';
}

const props = withDefaults(defineProps<Props>(), {
  shiftPreset: 'STANDARD',
  selectedPosition: null,
});

const emit = defineEmits<{
  (e: 'select-position', pos: PlayerPosition): void;
}>();

// Field positioning percentages tailored by defensive shift preset
const shiftCoordinates = computed<Record<PlayerPosition, { top: string; left: string; label: string }>>(() => {
  const base = {
    P: { top: '56%', left: '50%', label: 'Pitcher (P)' },
    C: { top: '88%', left: '50%', label: 'Catcher (C)' },
    '1B': { top: '50%', left: '74%', label: '1st Base (1B)' },
    '2B': { top: '34%', left: '60%', label: '2nd Base (2B)' },
    '3B': { top: '50%', left: '26%', label: '3rd Base (3B)' },
    SS: { top: '34%', left: '40%', label: 'Shortstop (SS)' },
    LF: { top: '20%', left: '22%', label: 'Left Field (LF)' },
    CF: { top: '12%', left: '50%', label: 'Center Field (CF)' },
    RF: { top: '20%', left: '78%', label: 'Right Field (RF)' },
    DH: { top: '88%', left: '84%', label: 'Designated Hitter (DH)' },
    UTIL: { top: '88%', left: '16%', label: 'Utility (UTIL)' },
  };

  if (props.shiftPreset === 'INFIELD_IN') {
    return {
      ...base,
      '1B': { top: '58%', left: '68%', label: '1st Base (In)' },
      '2B': { top: '44%', left: '56%', label: '2nd Base (In)' },
      '3B': { top: '58%', left: '32%', label: '3rd Base (In)' },
      SS: { top: '44%', left: '44%', label: 'Shortstop (In)' },
    };
  }

  if (props.shiftPreset === 'PULL_SHIFT') {
    return {
      ...base,
      SS: { top: '38%', left: '52%', label: 'Shortstop (Rover)' },
      '2B': { top: '32%', left: '68%', label: '2nd Base (Deep)' },
      LF: { top: '16%', left: '32%', label: 'Left Field' },
      CF: { top: '14%', left: '62%', label: 'Center Field' },
      RF: { top: '24%', left: '86%', label: 'Right Field' },
    };
  }

  if (props.shiftPreset === 'NO_DOUBLES') {
    return {
      ...base,
      '1B': { top: '48%', left: '80%', label: '1st Base (Line)' },
      '3B': { top: '48%', left: '20%', label: '3rd Base (Line)' },
      LF: { top: '10%', left: '20%', label: 'Left Field (Deep)' },
      CF: { top: '6%', left: '50%', label: 'Center Field (Deep)' },
      RF: { top: '10%', left: '80%', label: 'Right Field (Deep)' },
    };
  }

  return base;
});
</script>

<template>
  <div class="relative w-full aspect-[4/3] max-w-xl mx-auto rounded-3xl bg-gradient-to-b from-emerald-950/80 via-slate-900 to-slate-950 border border-slate-800 p-4 shadow-2xl overflow-hidden select-none">
    <!-- Diamond SVG outfield and base paths -->
    <svg class="absolute inset-0 w-full h-full opacity-30 pointer-events-none" viewBox="0 0 400 300">
      <!-- Outfield fence arc -->
      <path d="M 30 180 A 185 185 0 0 1 370 180" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="6,6" />
      <!-- Infield Dirt Diamond -->
      <polygon points="200,80 290,160 200,240 110,160" fill="none" stroke="#f59e0b" stroke-width="2" />
      <!-- Base markers -->
      <rect x="195" y="75" width="10" height="10" transform="rotate(45 200 80)" fill="#f8fafc" />
      <rect x="285" y="155" width="10" height="10" transform="rotate(45 290 160)" fill="#f8fafc" />
      <rect x="195" y="235" width="10" height="10" transform="rotate(45 200 240)" fill="#f8fafc" />
      <rect x="105" y="155" width="10" height="10" transform="rotate(45 110 160)" fill="#f8fafc" />
      <!-- Pitcher's Mound Circle -->
      <circle cx="200" cy="168" r="14" fill="rgba(245,158,11,0.15)" stroke="#f59e0b" stroke-width="1.5" />
    </svg>

    <!-- Shift Indicator Tag -->
    <div class="absolute top-3 left-4 text-[10px] font-extrabold uppercase tracking-wider text-emerald-400/80 bg-slate-950/60 px-2.5 py-1 rounded-lg border border-slate-800">
      Shift: {{ shiftPreset.replace('_', ' ') }}
    </div>

    <!-- Field Position Badges -->
    <div
      v-for="(coords, posKey) in shiftCoordinates"
      :key="posKey"
      class="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer group transition-all duration-300"
      :style="{ top: coords.top, left: coords.left }"
      @click="emit('select-position', posKey as PlayerPosition)"
    >
      <div
        :class="[
          'px-2.5 py-1.5 rounded-xl border text-center transition-all duration-200 transform group-hover:scale-110 shadow-lg backdrop-blur-md',
          selectedPosition === posKey
            ? 'bg-sky-500 text-slate-950 ring-4 ring-sky-400/40 border-white'
            : assignments[posKey as PlayerPosition]
              ? 'bg-slate-900/95 border-sky-500/60 text-sky-200 ring-2 ring-sky-500/20 hover:border-sky-400'
              : 'bg-slate-900/80 border-slate-700/80 text-slate-400 hover:border-slate-500 hover:text-slate-200'
        ]"
      >
        <span
          :class="[
            'text-[9px] font-black uppercase tracking-wider block',
            selectedPosition === posKey ? 'text-slate-950' : 'opacity-70'
          ]"
        >
          {{ posKey }}
        </span>
        <span
          :class="[
            'text-xs font-bold truncate max-w-[85px] block font-sans',
            selectedPosition === posKey ? 'text-slate-950 font-extrabold' : ''
          ]"
        >
          {{ assignments[posKey as PlayerPosition] ? `#${assignments[posKey as PlayerPosition]?.jerseyNumber} ${assignments[posKey as PlayerPosition]?.lastName}` : 'Empty' }}
        </span>
      </div>
    </div>
  </div>
</template>